import { Colors, Icon, Pills, Text, Toggle } from '@amelio/ui-library';
import { useId, useState } from 'react';

import type { TranslationKey } from '@/i18n-key';
import type { PillsAudienceVariant } from '@amelio/ui-library';
import type { Descendant } from 'slate';

import { useAmelioTranslations } from '@/hooks';
import { CustomizeSurveyEmail } from '@/pages/shared/ScheduleCreation/components/CustomizeSurveyEmail/CustomizeSurveyEmail';

export const CommunicationEmailAudience = {
    MANAGER: 'manager',
    EMPLOYEE: 'employee',
} as const;

export type CommunicationEmailAudienceValue =
    (typeof CommunicationEmailAudience)[keyof typeof CommunicationEmailAudience];

const cycleAudienceManagerFrameGradient = 'bg-gradient-to-r from-primary/60 to-primary/30';
const cycleAudienceEmployeeFrameGradient =
    'bg-gradient-to-r from-purple/40 via-secondary-800/40 to-purple/40';
// Flat fill, not a gradient — reuses the "FOR EMPLOYEES" pill's own hue (Pills.tsx `employeeAudience`), no new token.
const cycleAudienceManagerTileFill = 'bg-primary';
const cycleAudienceEmployeeTileFill = 'bg-purple';

// Numbers rather than classes: both reach the DOM as inline styles -- the tile's own `style`, and
// `Icon`, which writes a numeric `size` that way and that no class can override.
const ENVELOPE_TILE_SIZE_PX = 40;
const ENVELOPE_ICON_SIZE_PX = 20;

interface AudienceSkin {
    frameGradient: string;
    tileFill: string;
    pillsVariant: PillsAudienceVariant;
    labelKey: TranslationKey;
    titleKey: TranslationKey;
}

const AUDIENCE_SKIN: Record<CommunicationEmailAudienceValue, AudienceSkin> = {
    [CommunicationEmailAudience.MANAGER]: {
        frameGradient: cycleAudienceManagerFrameGradient,
        tileFill: cycleAudienceManagerTileFill,
        pillsVariant: 'managerAudience',
        labelKey: 'cycleEvaluationCommunications.audience.manager',
        titleKey: 'cycleEvaluationCommunications.email.titleManager',
    },
    [CommunicationEmailAudience.EMPLOYEE]: {
        frameGradient: cycleAudienceEmployeeFrameGradient,
        tileFill: cycleAudienceEmployeeTileFill,
        pillsVariant: 'employeeAudience',
        labelKey: 'cycleEvaluationCommunications.audience.employee',
        titleKey: 'cycleEvaluationCommunications.email.titleEmployee',
    },
};

export interface CommunicationEmailCardProps {
    audience: CommunicationEmailAudienceValue;
    enabled: boolean;
    onEnabledChange: (enabled: boolean) => void;
    emailLanguage: string;
    emailTitle: string;
    emailMessage: Descendant[];
    editorKey?: string;
    testEmailAddress: string;
    onEmailLanguageChange: (value: string) => void;
    onEmailTitleChange: (value: string) => void;
    onEmailMessageChange: (value: Descendant[]) => void;
    onTestEmailAddressChange: (value: string) => void;
    onTestEmail: () => void;
    isSendingTestEmail: boolean;
    isReadOnly?: boolean;
    /** See `CustomizeSurveyEmailProps.unavailableReason`. Forwarded untouched through `emailProps`. */
    unavailableReason?: string;
}

export const CommunicationEmailCard = ({
    audience,
    enabled,
    onEnabledChange,
    isReadOnly = false,
    ...emailProps
}: CommunicationEmailCardProps) => {
    const { t } = useAmelioTranslations();
    const [isExpanded, setIsExpanded] = useState(false);
    const panelId = useId();

    const skin = AUDIENCE_SKIN[audience];
    const audienceLabel = t(skin.labelKey);
    const isOpen = isExpanded && enabled;

    const handleEnabledChange = (next: boolean) => {
        if (isReadOnly) {
            return;
        }

        onEnabledChange(next);

        if (!next) {
            setIsExpanded(false);
        }
    };

    // Opens/closes on a click anywhere in the header; the chevron stays a real `<button>` for
    // keyboard activation, and its click bubbles here too so the toggle logic fires once, not twice.
    const handleHeaderClick = () => {
        if (enabled) {
            setIsExpanded((current) => !current);
        }
    };

    return (
        <div
            className={`overflow-hidden rounded-xl p-[1.5px] transition-all ${skin.frameGradient} ${isOpen ? 'shadow-xl' : 'shadow-sm'} ${enabled ? '' : 'opacity-60 grayscale-[0.4]'}`}
            data-testid={`communication-email-card-${audience}`}
        >
            <div className={'overflow-hidden rounded-[10px] bg-white/95'}>
                <div
                    onClick={handleHeaderClick}
                    className={`flex items-center gap-4 bg-white/60 py-4 pr-6 pl-7 ${isOpen ? 'border-base-100 border-b' : ''} ${enabled ? 'cursor-pointer' : ''}`}
                >
                    <div className={'flex min-w-0 flex-1 items-center gap-3'}>
                        <span
                            aria-hidden={'true'}
                            style={{ width: ENVELOPE_TILE_SIZE_PX, height: ENVELOPE_TILE_SIZE_PX }}
                            className={`flex shrink-0 items-center justify-center rounded-xl shadow-md ${skin.tileFill}`}
                        >
                            <Icon
                                name={'messages-sms'}
                                size={ENVELOPE_ICON_SIZE_PX}
                                color={Colors.WHITE}
                            />
                        </span>

                        <div className={'flex min-w-0 flex-col items-start gap-1'}>
                            <Pills
                                variant={skin.pillsVariant}
                                size={'audience'}
                            >
                                {audienceLabel}
                            </Pills>

                            <Text
                                as={'h4'}
                                size={'small'}
                                weight={'semibold'}
                            >
                                {t(skin.titleKey)}
                            </Text>
                        </div>
                    </div>

                    {/* Its own click never opens/closes the card — enabling an email is a
                        separate decision from viewing its content. */}
                    <div onClick={(event) => event.stopPropagation()}>
                        <Toggle
                            checked={enabled}
                            onChange={handleEnabledChange}
                            readOnly={isReadOnly}
                            size={'smPlus'}
                            ariaLabel={t('cycleEvaluationCommunications.email.enableAriaLabel', {
                                audience: audienceLabel,
                            })}
                            dataTestId={`communication-email-toggle-${audience}`}
                        />
                    </div>

                    <button
                        type={'button'}
                        disabled={!enabled}
                        aria-expanded={isOpen}
                        aria-controls={panelId}
                        aria-label={t('cycleEvaluationCommunications.email.expandAriaLabel')}
                        data-testid={`communication-email-expand-${audience}`}
                        className={
                            'hover:bg-base-50 shrink-0 cursor-pointer rounded-md p-1 transition-colors disabled:cursor-not-allowed'
                        }
                    >
                        <Icon
                            name={'arrow-arrow-down'}
                            size={'md'}
                            color={Colors.BASE_400}
                            strokeWidth={2}
                            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
                        />
                    </button>
                </div>

                <div id={panelId}>
                    {isOpen ? (
                        <CustomizeSurveyEmail
                            {...emailProps}
                            isReadOnly={isReadOnly}
                            skin={'cycleCommunications'}
                            testLaunchEmailLabel={t('cycleEvaluationCommunications.email.sendTest')}
                        />
                    ) : null}

                    {enabled ? null : (
                        <Text
                            size={'small'}
                            color={Colors.MUTED}
                            className={'px-7 pb-4'}
                        >
                            {t('cycleEvaluationCommunications.email.disabledNotice')}
                        </Text>
                    )}
                </div>
            </div>
        </div>
    );
};
